import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import { Bell } from 'lucide-react';
import { cn } from '@/utils/cn';
import { navTestId } from '@/utils/testId';
import { Tooltip } from '@/components/ui/Tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import ProfilePopover from '@/features/profile/ProfilePopover';
import NotificationsPopover from '@/features/notifications/NotificationsPopover';
import Logo from '@/images/logoMoiry.svg';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon, label, active, onClick }) => (
  <Tooltip content={label} side="right">
    <button
      onClick={onClick}
      data-testid={`sidebar-nav-${navTestId(label)}-btn`}
      className={cn(
        'group relative w-full flex flex-col items-center gap-1 px-2 py-2.5 transition-all',
        active ? 'text-chrome-active-fg' : 'text-chrome-fg-dim hover:text-chrome-fg'
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-[30px] -translate-y-1/2 w-[3px] rounded-r-full bg-chrome-accent transition-all',
          active ? 'h-8' : 'h-0'
        )}
      />
      <span
        className={cn(
          'h-10 w-10 rounded-lg flex items-center justify-center transition-all',
          active ? 'bg-chrome-active-bg' : 'group-hover:bg-chrome-hover'
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          'text-[10px] font-medium tracking-wide leading-none transition-opacity',
          active ? 'opacity-100' : 'opacity-80'
        )}
      >
        {label}
      </span>
    </button>
  </Tooltip>
);

interface AppSidebarProps {
  menuItems?: any[];
  activeRoute?: string;
  onNavigate?: (route: string) => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  menuItems = [],
  activeRoute = '',
  onNavigate,
}) => {
  const history = useHistory();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const unreadCount = useSelector((state: any) => state.notifications?.unreadCount ?? 0);

  return (
    <aside
      className="w-[72px] flex flex-col items-stretch shrink-0 relative overflow-visible"
      style={{ background: 'var(--chrome-rail)' }}
    >
      {/* Grid texture */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      />
      <div className="absolute top-0 bottom-0 right-0 w-px bg-black/20 pointer-events-none" />

      {/* Brand mark — logo MOIRY */}
      <div
        className="h-14 flex items-center justify-center cursor-pointer"
        onClick={() => history.push('/app/dashboard')}
        data-testid="sidebar-brand-logo-btn"
      >
        <img alt="MOIRY" src={Logo} className="h-5 brightness-0 invert" />
      </div>

      <div className="h-px border-b border-chrome-line mx-3" />

      <nav className="relative flex-1 flex flex-col py-3 gap-1 overflow-hidden">
        {menuItems.map((item: any, i: number) => {
          const isActive = activeRoute.startsWith(item.route_1);
          return (
            <SidebarItem
              key={item.id || i}
              icon={
                item.icon_svg ? (
                  <SVG
                    src={item.icon_svg}
                    className={cn('h-[18px] w-[18px]', isActive ? 'fill-white' : 'fill-white/70')}
                  />
                ) : (
                  <span className="h-[18px] w-[18px]" />
                )
              }
              label={item.title}
              active={isActive}
              onClick={() => onNavigate?.(item.route_1)}
            />
          );
        })}
      </nav>

      <div className="flex justify-center pb-1">
        <span className="text-[9px] font-mono text-white/60">v{__APP_VERSION__}</span>
      </div>

      <div className="h-px border-b border-chrome-line mx-3" />

      <div className="relative py-3 flex flex-col items-center gap-1">
        <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          <Tooltip content="Notifikace" side="right">
            <PopoverTrigger asChild>
              <button
                data-testid="sidebar-notifications-btn"
                className="relative h-9 w-9 rounded-lg flex items-center justify-center text-chrome-fg-dim hover:text-chrome-fg hover:bg-chrome-hover transition-colors"
              >
                <Bell size={16} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-chrome-accent text-white text-[9px] font-semibold leading-4 text-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
          </Tooltip>
          <PopoverContent className="w-80 p-0" align="start" side="right" sideOffset={12}>
            <NotificationsPopover onNavigate={() => setNotificationsOpen(false)} />
          </PopoverContent>
        </Popover>

        <ProfilePopover history={history} />
      </div>
    </aside>
  );
};

export default AppSidebar;
